import { inject, Injectable } from '@angular/core'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { FeatureName, ProductFeature } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemsState, FetchCourtSystemProductFeaturesAction } from '@ftr/data-court-system'
import { ErrorPageService, isNotNullOrUndefined, titleCase, unwrapData } from '@ftr/foundation'
import { APP_MODE, AppPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { HelpMenuItemsProvider } from '@ftr/ui-help'
import { FavoritesState, FetchFavoritesForCourtSystemAction, UserState } from '@ftr/ui-user'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { Store } from '@ngxs/store'
import { combineLatest, filter, map, Observable, of, switchMap, tap } from 'rxjs'
import { CourtSystemNavItems } from '../court-system-nav-items'
import { toSideNavItem } from '../help-support'
import { getGlobalAdminNavItem, SideNavGroup, SideNavItem } from '../side-nav-item'
import { SideNavProviderResult } from '../side-nav-provider-result'
import { IconUrlMapperService } from './icon-mapping-service'

const ADMIN_PAGE_PERMISSIONS = [
  UserGroupPermissionId.ManageRealtimeRecorders,
  UserGroupPermissionId.ConfigureCourtSystem,
  UserGroupPermissionId.ConfigureDepartments,
  UserGroupPermissionId.AdministerLocations,
  UserGroupPermissionId.AdministerHolidays,
  UserGroupPermissionId.RemoveTrms,
  UserGroupPermissionId.AdministerBillingGroups,
  UserGroupPermissionId.AccessAuditLogs,
]

@Injectable({
  providedIn: 'root',
})
export class InternalUserNavItemsProvider {
  private readonly store = inject(Store)
  private readonly courtSystemNavItems = inject(CourtSystemNavItems)
  private readonly iconMapperService = inject(IconUrlMapperService)
  private readonly errorPageService = inject(ErrorPageService)
  private readonly vocabularyTermsService = inject(VocabularyTermsService)
  private readonly helpMenuItemsProvider = inject(HelpMenuItemsProvider)
  private readonly appMode = inject(APP_MODE)

  provide(currentCourtSystemId: Uuid): Observable<SideNavProviderResult> {
    const helpSupportFlyoutItems = this.helpMenuItemsProvider.provide({
      mode: 'courts',
      courtSystemId: currentCourtSystemId,
    })
    this.store.dispatch(new FetchCourtSystemProductFeaturesAction(currentCourtSystemId))
    const userPermissionsAndGlobalAdmin = combineLatest([
      this.store.select(UserState.getEnabledPermissions).pipe(map(fn => fn(currentCourtSystemId).map(p => p.id))),
      this.store.select(UserState.isGlobalAdmin),
    ])

    return combineLatest([
      userPermissionsAndGlobalAdmin.pipe(
        tap(([userPermissionIds, isGlobalAdmin]) => {
          if (!isGlobalAdmin && userPermissionIds.length === 0) {
            this.errorPageService.showNotFoundPage()
          }
        }),
      ),
      this.store.select(UserState.currentCourtSystemEnabledFeatures).pipe(unwrapData()),
      this.vocabularyTermsService.observeTerms(currentCourtSystemId).pipe(filter(isNotNullOrUndefined)),
      this.store.select(UserState.user).pipe(
        unwrapData(),
        switchMap(user => {
          this.store.dispatch(new FetchFavoritesForCourtSystemAction(currentCourtSystemId))
          return of(user)
        }),
        switchMap(() => {
          return combineLatest([
            this.store.select(FavoritesState.favorites(currentCourtSystemId)),
            this.store.select(FavoritesState.isLoading),
          ])
        }),
        map(([favorites, favoritesLoading]) => ({ favoritesLoading, favorites })),
      ),
      this.store.select(UserState.currentCourtSystemConfiguration).pipe(unwrapData()),
      this.store.select(CourtSystemsState.courtSystemProductFeatures(currentCourtSystemId)),
    ]).pipe(
      map(
        ([
          [userPermissionIds, isGlobalAdmin],
          currentCourtSystemEnabledFeatures,
          vocabularyTerms,
          favoritesConfiguration,
          courtSystemConfiguration,
          currentCourtSystemProductFeatures,
          // eslint-disable-next-line complexity
        ]) => {
          const isCourtsApp = this.appMode() === 'courts'
          const navGroups: SideNavGroup[] = []
          const footerItems: SideNavItem[] = []

          const resourceGroup: SideNavGroup = {
            items: [],
          }

          resourceGroup.items?.push({
            type: 'LINK',
            icon: 'Courthouse',
            title: titleCase(vocabularyTerms.courthouse.plural),
            path: [`/${AppPaths.CourtSystem}`, currentCourtSystemId, CourtSystemPaths.Locations],
          } as SideNavItem)

          if (courtSystemConfiguration.isDepartmentsEnabled) {
            resourceGroup.items?.push({
              type: 'LINK',
              icon: 'Department',
              ...this.courtSystemNavItems.departments(currentCourtSystemId, vocabularyTerms),
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.PlaybackHearings)) {
            resourceGroup.items?.push({
              type: 'LINK',
              icon: 'Case',
              ...this.courtSystemNavItems.cases(currentCourtSystemId, vocabularyTerms),
            } as SideNavItem)
          }

          if (currentCourtSystemProductFeatures.includes(ProductFeature.JudgeAnnotationsLibrary)) {
            resourceGroup.items?.push({
              type: 'LINK',
              icon: 'Judge',
              ...this.courtSystemNavItems.judges(currentCourtSystemId, vocabularyTerms),
            } as SideNavItem)
          }

          if (resourceGroup.items?.length) {
            navGroups.push(resourceGroup)
          }

          navGroups.push({
            title: 'Favorites',
            isLoading: favoritesConfiguration.favoritesLoading,
            items: favoritesConfiguration.favorites?.map(favorite => {
              const [path, queryParams] = urlToRouteLinkArray(favorite.url)
              return {
                type: 'LINK',
                icon: this.iconMapperService.getIconForUrl(favorite.url),
                title: favorite.label,
                path,
                queryParams,
              } as SideNavItem
            }),
          })

          if (userPermissionIds.includes(UserGroupPermissionId.PlaybackRecordings)) {
            navGroups.push({
              title: 'Content',
              items: [
                {
                  type: 'LINK',
                  icon: 'Recordings',
                  ...this.courtSystemNavItems.recordings(currentCourtSystemId),
                } as SideNavItem,
              ],
            })
          }

          if (isCourtsApp) {
            const requestProcessingGroup: SideNavGroup = {
              title: 'Request Processing',
              items: [],
            }

            if (userPermissionIds.includes(UserGroupPermissionId.ProcessAudioOrders)) {
              requestProcessingGroup.items?.push({
                type: 'LINK',
                icon: 'RecordingRequest',
                ...this.courtSystemNavItems.recordingRequests(currentCourtSystemId),
                title: 'Recording Requests',
                tooltipHint: 'Recording Request Processing',
              } as SideNavItem)
            }

            if (userPermissionIds.includes(UserGroupPermissionId.ProcessTranscriptOrders)) {
              requestProcessingGroup.items?.push({
                type: 'LINK',
                icon: 'TranscriptOrder',
                ...this.courtSystemNavItems.transcriptOrders(currentCourtSystemId),
                title: 'Transcript Requests',
                tooltipHint: 'Transcript Request Processing',
              } as SideNavItem)
            }

            if (requestProcessingGroup.items?.length) {
              navGroups.push(requestProcessingGroup)
            }
          } else {
            const orderProcessingGroup: SideNavGroup = {
              title: 'Order Processing',
              items: [],
            }

            if (userPermissionIds.includes(UserGroupPermissionId.ProcessAudioOrders)) {
              orderProcessingGroup.items?.push({
                type: 'LINK',
                icon: 'AudioOrder',
                ...this.courtSystemNavItems.audioOrders(currentCourtSystemId),
                title: 'Audio',
                tooltipHint: 'Audio Order Processing',
              } as SideNavItem)
            }

            if (userPermissionIds.includes(UserGroupPermissionId.ProcessRealTimeOrders)) {
              orderProcessingGroup.items?.push({
                type: 'LINK',
                icon: 'RealtimeOrder',
                ...this.courtSystemNavItems.realTimeOrders(currentCourtSystemId),
                title: 'Live',
                tooltipHint: 'Live Order Processing',
              } as SideNavItem)
            }

            if (userPermissionIds.includes(UserGroupPermissionId.ProcessTranscriptOrders)) {
              orderProcessingGroup.items?.push({
                type: 'LINK',
                icon: 'TranscriptOrder',
                ...this.courtSystemNavItems.transcriptOrders(currentCourtSystemId),
                title: 'Transcript',
                tooltipHint: 'Transcript Order Processing',
              } as SideNavItem)
            }

            if (orderProcessingGroup.items?.length) {
              navGroups.push(orderProcessingGroup)
            }
          }

          const managementGroup: SideNavGroup = {
            title: 'Management',
            items: [],
          }

          const isRealTimeRecordersEnabled = !!currentCourtSystemEnabledFeatures?.some(
            feature => feature.name === FeatureName.RealTimeRecorders,
          )

          if (
            (isGlobalAdmin && isRealTimeRecordersEnabled) ||
            (userPermissionIds.includes(UserGroupPermissionId.ConfigureCourtSystem) &&
              currentCourtSystemProductFeatures.includes(ProductFeature.CourtMonitoring))
          ) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'CourtMonitoring',
              ...this.courtSystemNavItems.courtMonitoring(currentCourtSystemId),
              title: 'Monitoring',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.ViewRealTimeRecorders) && isRealTimeRecordersEnabled) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'Recorder',
              ...this.courtSystemNavItems.producers(currentCourtSystemId),
              title: 'Recorders',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.AdministerUserGroups)) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'People',
              ...this.courtSystemNavItems.courtUsers(currentCourtSystemId),
              title: 'Users & Groups',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.WriteRecordingVault)) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'TrmRepositories',
              ...this.courtSystemNavItems.recordingVault(currentCourtSystemId),
              title: 'TRM Repositories',
            } as SideNavItem)
          }

          if (userPermissionIds.includes(UserGroupPermissionId.AccessReports)) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'BarGraphVertical',
              ...this.courtSystemNavItems.reports(currentCourtSystemId),
              title: 'Reports',
            } as SideNavItem)
          }

          if (userPermissionIds.some(id => ADMIN_PAGE_PERMISSIONS.includes(id))) {
            managementGroup.items?.push({
              type: 'LINK',
              icon: 'Settings',
              ...this.courtSystemNavItems.adminMenu(currentCourtSystemId),
              title: 'Settings',
            } as SideNavItem)
          }

          if (managementGroup.items?.length) {
            navGroups.push(managementGroup)
          }

          if (isGlobalAdmin) {
            footerItems.push(getGlobalAdminNavItem(currentCourtSystemId))
          }

          footerItems.push({
            type: 'BRANCH',
            title: 'Help & Support',
            icon: 'HelpCircleSolid',
            displayMode: 'OVERLAY',
            children: helpSupportFlyoutItems().map(toSideNavItem),
          } as SideNavItem)

          return { navGroups, footerItems }
        },
      ),
    )
  }
}

const urlToRouteLinkArray = (url: string): any[] => {
  const [path, searchString] = url.split('?')
  if (!searchString) {
    return [path]
  }
  const searchParams = new URLSearchParams(searchString)
  const queryObject: Record<string, string> = {}
  for (const [key, value] of searchParams) {
    queryObject[key] = value
  }
  return [path, queryObject]
}
